import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFilm, 
  faUsers, 
  faEye, 
  faHeart,
  faArrowUp,
  faArrowDown,
  faChartLine,
  faBell,
  faCog
} from '@fortawesome/free-solid-svg-icons';
import '../styles/AdminDashboard.css';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalAnimes: 0,
    totalMembers: 0,
    totalViews: 0,
    totalFavorites: 0,
    recentAnimes: [],
    popularAnimes: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch anime statistics
      const animeResponse = await fetch('https://phimapi.com/v1/api/danh-sach/hoat-hinh?page=1&country=nhat-ban');
      const animeData = await animeResponse.json();
      
      // Simulate member and view statistics (in real app, this would come from your backend)
      const mockStats = {
        totalMembers: 1247,
        totalViews: 45632,
        totalFavorites: 3421
      };

      if (animeData?.data) {
        setStats({
          totalAnimes: animeData.data.params.pagination.totalItems || 0,
          ...mockStats,
          recentAnimes: animeData.data.items?.slice(0, 5) || [],
          popularAnimes: animeData.data.items?.slice(5, 10) || []
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon, title, value, change, changeType, color }) => (
    <div className={`stat-card ${color}`}>
      <div className="stat-icon">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="stat-content">
        <h3>{title}</h3>
        <div className="stat-value">{value.toLocaleString()}</div>
        {change && (
          <div className={`stat-change ${changeType}`}>
            <FontAwesomeIcon icon={changeType === 'up' ? faArrowUp : faArrowDown} />
            <span>{change}%</span>
          </div>
        )}
      </div>
    </div>
  );

  const AnimeListItem = ({ anime, index }) => (
    <div className="anime-list-item">
      <div className="anime-rank">#{index + 1}</div>
      <div className="anime-poster">
        <img 
          src={`https://phimimg.com/${anime.poster_url}`} 
          alt={anime.name}
          onError={(e) => {
            e.target.src = '/logo.png';
          }}
        />
      </div>
      <div className="anime-info">
        <h4>{anime.name}</h4>
        <p>{anime.origin_name}</p>
        <div className="anime-meta">
          <span className="year">{anime.year}</span>
          <span className="quality">{anime.quality}</span>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Đang tải dữ liệu...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h2>Tổng quan hệ thống</h2>
        <p>Thống kê tổng quan về website MAnime</p>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <StatCard
          icon={faFilm}
          title="Tổng số Anime"
          value={stats.totalAnimes}
          change={12.5}
          changeType="up"
          color="blue"
        />
        <StatCard
          icon={faUsers}
          title="Thành viên"
          value={stats.totalMembers}
          change={8.3}
          changeType="up"
          color="green"
        />
        <StatCard
          icon={faEye}
          title="Lượt xem"
          value={stats.totalViews}
          change={15.2}
          changeType="up"
          color="purple"
        />
        <StatCard
          icon={faHeart}
          title="Yêu thích"
          value={stats.totalFavorites}
          change={5.7}
          changeType="down"
          color="red"
        />
      </div>

      {/* Statistics Chart */}
      <div className="chart-section">
        <div className="chart-container">
          <div className="chart-header">
            <h3>Thống kê truy cập 7 ngày qua</h3>
            <div className="chart-legend">
              <div className="legend-item">
                <div className="legend-color blue"></div>
                <span>Lượt xem</span>
              </div>
              <div className="legend-item">
                <div className="legend-color green"></div>
                <span>Thành viên mới</span>
              </div>
            </div>
          </div>
          <div className="chart-area">
            <div className="chart-bars">
              <div className="chart-day">
                <div className="bars">
                  <div className="bar blue" style={{height: '60%'}} title="1,200 lượt xem"></div>
                  <div className="bar green" style={{height: '30%'}} title="15 thành viên mới"></div>
                </div>
                <span className="day-label">T2</span>
              </div>
              <div className="chart-day">
                <div className="bars">
                  <div className="bar blue" style={{height: '75%'}} title="1,500 lượt xem"></div>
                  <div className="bar green" style={{height: '45%'}} title="22 thành viên mới"></div>
                </div>
                <span className="day-label">T3</span>
              </div>
              <div className="chart-day">
                <div className="bars">
                  <div className="bar blue" style={{height: '85%'}} title="1,700 lượt xem"></div>
                  <div className="bar green" style={{height: '35%'}} title="18 thành viên mới"></div>
                </div>
                <span className="day-label">T4</span>
              </div>
              <div className="chart-day">
                <div className="bars">
                  <div className="bar blue" style={{height: '90%'}} title="1,800 lượt xem"></div>
                  <div className="bar green" style={{height: '55%'}} title="28 thành viên mới"></div>
                </div>
                <span className="day-label">T5</span>
              </div>
              <div className="chart-day">
                <div className="bars">
                  <div className="bar blue" style={{height: '95%'}} title="1,900 lượt xem"></div>
                  <div className="bar green" style={{height: '65%'}} title="32 thành viên mới"></div>
                </div>
                <span className="day-label">T6</span>
              </div>
              <div className="chart-day">
                <div className="bars">
                  <div className="bar blue" style={{height: '100%'}} title="2,000 lượt xem"></div>
                  <div className="bar green" style={{height: '70%'}} title="35 thành viên mới"></div>
                </div>
                <span className="day-label">T7</span>
              </div>
              <div className="chart-day">
                <div className="bars">
                  <div className="bar blue" style={{height: '80%'}} title="1,600 lượt xem"></div>
                  <div className="bar green" style={{height: '40%'}} title="20 thành viên mới"></div>
                </div>
                <span className="day-label">CN</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;